import { useClickAway } from "@uidotdev/usehooks";
import * as d3 from "d3";
import { useAtom } from "jotai";
import { DateTime } from "luxon";
import { type Ref, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type {
    EmissionRecordView,
    SiteNonDetect,
} from "../../../../../../apiClient/generated";
import {
    useResizeObserver,
    useZoomOnPlot,
} from "../../../../../../ui/Plot/hooks";
import { formatDateTime } from "../../../../../../utils/dates";
import { EMISSION_COLORS } from "../../../../../MapV2/constants";
import { hoverContextAtom, selectionContextAtom } from "../../../state";
import { computePlotTicks, TIMELINE_MARGINS } from "../plot";
import { groupBridgerEmissions, type SiteTotal } from "./bridger";
import { EmissionPlotHover } from "./hover";

const dsMap = {
    THIRD_PARTY: "thirdParty",
    SELF_REPORTED: "operatorProvided",
    EPA: "epa",
};

interface EmissionPlotProps {
    data: EmissionRecordView[];
    nonDetects: SiteNonDetect[];
    dataDateRange: {
        dateBefore: Date;
        dateAfter: Date;
    };
    logScale: boolean;
    showIndividualEmissionsFromGroups: boolean;
    setCursorPosition?: (data?: { x: Date; y: number }) => void;
}

export const EmissionPlot = ({
    data,
    nonDetects,
    dataDateRange,
    logScale,
    showIndividualEmissionsFromGroups,
    setCursorPosition,
}: EmissionPlotProps) => {
    const svgRef = useRef();
    const containerRef = useRef<HTMLDivElement>();
    const dimensions = useResizeObserver(containerRef);

    // Hover context handling with external state
    const [hoverData, setHoverData] = useState<{
        emissionId?: string;
        groupedEmissionData?: {
            meanDate: Date;
            total: number;
            providerName: string;
            numberOfGroupedEmissions: number;
        };
        nonDetectId?: string;
    }>({});
    const [tooltipPosition, setTooltipPosition] = useState<{
        x: number;
        y: number;
    }>({ x: 0, y: 0 });
    // Global (page-wide) context hover
    const [sitePageHoverContext, setSitePageHoverContext] =
        useAtom(hoverContextAtom);

    // When clicking on multiple emissions, show dropdown
    const [pickerPosition, setPickerPosition] = useState<{
        x: number;
        y: number;
    }>();
    const [pickedEmissions, setPickedEmissions] = useState<
        EmissionRecordView[]
    >([]);

    // Selection context
    const [selectionContext, setSelectionContext] =
        useAtom(selectionContextAtom);

    // Close picker when clicking outside
    const pickerRef = useClickAway(() => {
        setPickerPosition(undefined);
        setPickedEmissions([]);
    });

    // Zoom controls
    const { zoomDomain, addZoomBrushHandle, resetZoom, currentTransform } =
        useZoomOnPlot({
            dimensions,
            svgRef,
            disableDoubleClickReset: true,
        });
    useEffect(() => {
        if (zoomDomain.x) {
            setSelectionContext((d) => ({
                ...d,
                dateRange: {
                    start: zoomDomain.x[0] as Date,
                    end: zoomDomain.x[1] as Date,
                },
            }));
        } else {
            setSelectionContext((d) => ({ ...d, dateRange: undefined }));
        }
    }, [zoomDomain, setSelectionContext]);

    // Filter out data points outside displaying range
    // and also groups bridger points if they are listed.
    const [emissions, groupedSiteTotals] = useMemo(() => {
        const filteredData = data.filter(
            (d) =>
                d.dataPoint.detectionTimestamp >= dataDateRange.dateAfter &&
                d.dataPoint.detectionTimestamp <= dataDateRange.dateBefore,
        );

        return groupBridgerEmissions(
            filteredData,
            showIndividualEmissionsFromGroups,
        );
    }, [showIndividualEmissionsFromGroups, data, dataDateRange]);

    useEffect(() => {
        const margin = {
            ...TIMELINE_MARGINS,
            bottom: 12,
            top: 1,
        };
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;
        const containerRect = containerRef.current.getBoundingClientRect();

        // Clear previous SVG content
        d3.select(svgRef.current).selectAll("*").remove();

        // Create SVG
        const svg = d3
            .select(svgRef.current)
            .attr("viewBox", `0 0 ${dimensions.width} ${dimensions.height}`)
            .attr("width", dimensions.width)
            .attr("height", dimensions.height);

        // Create the chart group
        const chart = svg
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        /**
         * Y Scale functions and definition
         */
        const yScaleFn = (useLogScale = true) => {
            const scale = useLogScale ? d3.scaleLog() : d3.scaleLinear();

            // Collect all values that need to be considered for domain
            const allValues = [];

            // Add emission data points with uncertainty
            const validData = data.filter((d) => !!d.dataPoint.detectedRate);
            validData.forEach((d) => {
                const uncertainty =
                    (d.dataPoint.detectedRateUncertainty || 0) / 1000;
                const rate = d.dataPoint.detectedRate / 1000;
                allValues.push(rate - uncertainty);
                allValues.push(rate + uncertainty);
            });

            // Also consider grouped emissions
            Object.values(groupedSiteTotals).forEach((d) => {
                allValues.push(d.total / 1000);
            });

            // Add non-detect detection limits if enabled
            nonDetects.forEach((d) => {
                allValues.push(d.detectionLimit / 1000);
            });

            // Calculate domain
            let minValue = d3.min(allValues);
            let maxValue = d3.max(allValues);

            // For log scale, ensure minimum value is positive and add padding
            if (useLogScale) {
                minValue = Math.max(minValue || 0.001, 0.001);
                // Extend domain by a factor for log scale
                minValue = minValue / 1.1;
                maxValue = maxValue * 1.1;
            } else {
                minValue = Math.max(minValue || 0, 0);
                // Add padding for linear scale
                const padding = (maxValue - minValue) * 0.1;
                minValue = Math.max(0, minValue - padding);
                maxValue = maxValue + padding;
            }

            // Apply zoom domain if it exists
            if (zoomDomain.y) {
                if (useLogScale) {
                    // For log scale, ensure minimum is positive and respect zoom domain
                    minValue = Math.max(0.001, zoomDomain.y[0]);
                    maxValue = Math.max(zoomDomain.y[1], minValue * 1.1);
                } else {
                    // For linear scale, constrain to 0 baseline
                    minValue = 0;
                    maxValue = Math.max(zoomDomain.y[1], 0.1);
                }
            }

            return scale
                .domain([minValue, maxValue])
                .nice()
                .range([height - margin.bottom, margin.top]);
        };

        const yScaleDetection = yScaleFn(logScale);

        const computeYTicks = (numberOfTicks: number) => {
            const [start, end] = yScaleDetection.domain();
            if (logScale) {
                const interval = 10;
                const length = Math.log10(end) - Math.log10(start) + 1;
                return Array.from({ length }, (_, i) => start * interval ** i);
            } else {
                const interval = (end - start) / (numberOfTicks - 1);
                return Array.from(
                    { length: numberOfTicks },
                    (_, i) => start + interval * i,
                );
            }
        };

        const yAxisDetection = d3
            .axisLeft(yScaleDetection)
            .tickSizeOuter(0)
            .tickSize(5)
            .tickFormat((d) => d3.format(",")(d))
            .tickValues(computeYTicks(6));

        const yGrid = d3
            .axisLeft(yScaleDetection)
            .ticks(5, ",")
            .tickSize(-width)
            .tickFormat(() => "");

        /**
         * X Axis functions and definitions
         */
        // Date range
        const dateRange = [dataDateRange.dateAfter, dataDateRange.dateBefore];
        const xScale = d3.scaleUtc().domain(dateRange).range([0, width]);

        const { tickFormat, tickValues } = computePlotTicks({
            startDate: dataDateRange.dateAfter,
            endDate: dataDateRange.dateBefore,
            plotWidth: width,
        });

        // X Axis with guaranteed start/end ticks
        chart
            .append("g")
            .attr("class", "x-axis-full")
            .attr("transform", `translate(0,${height - margin.bottom})`)
            .call(
                d3
                    .axisBottom(xScale)
                    .tickValues(tickValues)
                    .tickSize(5)
                    .tickSizeOuter(0)
                    .tickPadding(2)
                    .tickFormat(tickFormat),
            )
            .selectAll("text")
            .attr("font-size", "12px")
            .attr("fill", "#777");

        // Add grid lines for x-axis
        chart
            .append("g")
            .attr("class", "grid")
            .attr("transform", `translate(0,${height - margin.bottom})`)
            .call(
                d3
                    .axisBottom(xScale)
                    .tickValues(tickValues)
                    .tickSize(-height + margin.top + margin.bottom)
                    .tickSizeOuter(0)
                    .tickFormat(() => ""),
            )
            .selectAll("line")
            .style("stroke", "#e5e7eb")
            .style("stroke-dasharray", "6,6");

        // Add grid lines for y-axis
        chart
            .append("g")
            .attr("class", "grid")
            .call(yGrid)
            .call((g) => g.select(".domain").remove())
            .selectAll("line")
            .style("stroke", "#e5e7eb")
            .style("stroke-width", "0.5")
            .style("stroke-dasharray", "6,6");

        chart
            .append("g")
            .classed("", true)
            .call(yAxisDetection)
            .selectAll("text")
            .style("font-size", "14px")
            .attr("text-anchor", "center")
            .attr("dx", -3)
            .attr("dy", (_d, i, arr) => {
                if (i === 0) {
                    return 0;
                } else if (i === arr.length - 1) {
                    return 12;
                }
                return 5;
            });

        // Add y-axis label
        svg.append("text")
            .classed("text-gray-500", true)
            .attr("transform", `rotate(-90) translate(${-height / 2}, 15)`)
            .attr("x", 0)
            .attr("y", 0)
            .attr("text-anchor", "middle")
            .style("font-size", "14px")
            .text("kg/hr (methane)");

        // Zoom handle
        addZoomBrushHandle({
            chart,
            height,
            width,
            xScale,
            yScale: yScaleDetection,
            constrainYToZero: false,
        });

        /**
         * Data
         */

        // Error bars
        chart
            .append("g")
            .attr("class", "error-bar")
            .selectAll("line")
            .data(emissions)
            .enter()
            .filter((d) => d.dataPoint.detectedRate != null)
            .append("line")
            .attr("x1", (d) => xScale(d.dataPoint.detectionTimestamp))
            .attr("x2", (d) => xScale(d.dataPoint.detectionTimestamp))
            .attr("y1", (d) =>
                yScaleDetection(
                    (d.dataPoint.detectedRate +
                        d.dataPoint.detectedRateUncertainty) /
                        1000,
                ),
            )
            .attr("y2", (d) =>
                yScaleDetection(
                    (d.dataPoint.detectedRate -
                        d.dataPoint.detectedRateUncertainty) /
                        1000,
                ),
            )
            .attr("stroke", "black")
            .attr("stroke-width", 1)
            .style("opacity", (d) => {
                if (selectionContext) {
                    return d.id === selectionContext.emissionId ? 1 : 0;
                }
                if (sitePageHoverContext?.emissionIds?.includes(d.id)) {
                    return 1;
                }
                return 0;
            });

        // Add horizontal caps to the error bars (T-shape)
        chart
            .append("g")
            .attr("class", "error-bar")
            .selectAll("line")
            .data(emissions)
            .enter()
            .filter(
                (d) =>
                    d.dataPoint.detectedRate != null &&
                    d.dataPoint.detectedRateUncertainty != null,
            )
            .append("line")
            .attr("x1", (d) => xScale(d.dataPoint.detectionTimestamp) - 5)
            .attr("x2", (d) => xScale(d.dataPoint.detectionTimestamp) + 5)
            .attr("y1", (d) =>
                yScaleDetection(
                    (d.dataPoint.detectedRate +
                        d.dataPoint.detectedRateUncertainty) /
                        1000,
                ),
            )
            .attr("y2", (d) =>
                yScaleDetection(
                    (d.dataPoint.detectedRate +
                        d.dataPoint.detectedRateUncertainty) /
                        1000,
                ),
            )
            .attr("stroke", "black")
            .attr("stroke-width", 1)
            .style("opacity", (d) => {
                if (selectionContext) {
                    return d.id === selectionContext.emissionId ? 1 : 0;
                }
                if (sitePageHoverContext?.emissionIds?.includes(d.id)) {
                    return 1;
                }
                return 0;
            });

        chart
            .append("g")
            .attr("class", "error-bar")
            .selectAll("line")
            .data(emissions)
            .enter()
            .filter(
                (d) =>
                    d.dataPoint.detectedRate != null &&
                    d.dataPoint.detectedRateUncertainty != null,
            )
            .append("line")
            .attr("x1", (d) => xScale(d.dataPoint.detectionTimestamp) - 5)
            .attr("x2", (d) => xScale(d.dataPoint.detectionTimestamp) + 5)
            .attr("y1", (d) =>
                yScaleDetection(
                    (d.dataPoint.detectedRate -
                        d.dataPoint.detectedRateUncertainty) /
                        1000,
                ),
            )
            .attr("y2", (d) =>
                yScaleDetection(
                    (d.dataPoint.detectedRate -
                        d.dataPoint.detectedRateUncertainty) /
                        1000,
                ),
            )
            .attr("stroke", "black")
            .attr("stroke-width", 1)
            .style("opacity", (d) => {
                if (selectionContext) {
                    return d.id === selectionContext.emissionId ? 1 : 0;
                }
                if (sitePageHoverContext?.emissionIds?.includes(d.id)) {
                    return 1;
                }
                return 0;
            });

        // First, define a pattern for elements without infrastructure
        chart
            .append("defs")
            .selectAll("pattern")
            .data(Object.entries(EMISSION_COLORS))
            .enter()
            .append("pattern")
            .attr("id", ([key]) => `noInfrastructurePattern-${key}`)
            .attr("patternUnits", "userSpaceOnUse")
            .attr("width", 4)
            .attr("height", 4)
            .each(function ([, color]) {
                // Add white background rectangle
                d3.select(this)
                    .append("rect")
                    .attr("width", 4)
                    .attr("height", 4)
                    .attr("fill", "white");

                // Add the diagonal lines pattern
                d3.select(this)
                    .append("path")
                    .attr("d", "M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2")
                    .attr("stroke", `rgb(${color.join(",")})`)
                    .attr("stroke-width", 1);
            });

        // Create a group for both the visible line and hover area
        const concentrationLines = chart
            .append("g")
            .selectAll("g")
            .data(emissions)
            .enter()
            .filter(
                (d) =>
                    d.dataPoint.concentration != null &&
                    !d.dataPoint.detectedRate,
            )
            .append("g");

        const nonDetectLines = chart
            .append("g")
            .selectAll("g")
            .data(
                nonDetects.filter(
                    (nd) =>
                        nd.timestamp >= dataDateRange.dateAfter &&
                        nd.timestamp <= dataDateRange.dateBefore,
                ),
            )
            .enter()
            .append("g");

        nonDetectLines
            .append("line")
            .attr("x1", (d) => xScale(d.timestamp))
            .attr("x2", (d) => xScale(d.timestamp))
            .attr("y1", margin.top)
            .attr("y2", (d) => {
                const detectionLimit = d.detectionLimit / 1000;
                const [minDomain, maxDomain] = yScaleDetection.domain();
                const clampedLimit = Math.min(
                    Math.max(detectionLimit, minDomain),
                    maxDomain,
                );
                return yScaleDetection(clampedLimit);
            })
            .attr("stroke", () => {
                return "#4096FF";
            })
            .attr("stroke-width", () => {
                return 7;
            })
            .style("opacity", 0.3)
            .on("mouseover", (event, d: SiteNonDetect) => {
                const [px, py] = d3.pointer(event, containerRef.current);
                const rect = containerRef.current.getBoundingClientRect();
                setTooltipPosition({ x: rect.left + px, y: rect.top + py });
                setHoverData({ nonDetectId: d.id });
            })
            .on("mouseout", () => {
                setHoverData({});
            });

        // Add the visible dashed line
        concentrationLines
            .append("line")
            .attr("x1", (d) => xScale(d.dataPoint.detectionTimestamp))
            .attr("x2", (d) => xScale(d.dataPoint.detectionTimestamp))
            .attr("y1", margin.top)
            .attr("y2", height - margin.bottom)
            .attr("stroke", (d) => {
                const color = EMISSION_COLORS[dsMap[d.dataPoint.dataSource]];
                return color ? `rgb(${color.join(",")})` : "#000";
            })
            .attr("stroke-width", (d) => {
                if (selectionContext) {
                    return selectionContext.emissionId === d.id ? 2.5 : 1.5;
                }
                return 1;
            })
            .attr("stroke-dasharray", (d) => (d.site ? "6,6" : "4,4"))
            .style("opacity", 0.5)
            .on("click", (event, d: EmissionRecordView) => {
                event.stopPropagation();
                const emissionTimestamp = DateTime.fromJSDate(
                    d.dataPoint.detectionTimestamp,
                );
                setSelectionContext((s) => ({
                    ...s,
                    emissionId: d.id,
                    dateRange: {
                        start: emissionTimestamp.minus({ days: 7 }).toJSDate(),
                        end: emissionTimestamp.plus({ days: 7 }).toJSDate(),
                    },
                    highlightPosition: {
                        date: emissionTimestamp.toJSDate(),
                        x: 0,
                    },
                }));
                setTimeout(() => setHoverData({}), 400);
            })
            .on("mouseover", (event, d) => {
                const [px, py] = d3.pointer(event, containerRef.current);
                const rect = containerRef.current.getBoundingClientRect();
                setTooltipPosition({ x: rect.left + px, y: rect.top + py });
                setHoverData({ emissionId: d.id });
            })
            .on("mouseout", () => {
                setHoverData({});
            });

        // Add the invisible wider line for easier hovering
        concentrationLines
            .append("line")
            .attr("x1", (d) => xScale(d.dataPoint.detectionTimestamp))
            .attr("x2", (d) => xScale(d.dataPoint.detectionTimestamp))
            .attr("y1", margin.top)
            .attr("y2", height - margin.bottom)
            .style("cursor", "pointer")
            .attr("stroke", (d) => {
                if (selectionContext) {
                    return selectionContext.emissionId === d.id
                        ? "#00000010"
                        : "transparent";
                }
                return "transparent";
            })
            .attr("stroke-width", 10)
            .style("cursor", "pointer");

        // Add site total points
        chart
            .append("g")
            .selectAll("circle")
            .data(Object.values(groupedSiteTotals))
            .join("circle")
            .attr("cx", (d) => xScale(d.meanDate || new Date(d.date)))
            .attr("cy", (d) => yScaleDetection(d.total / 1000))
            .attr("r", 5)
            .attr("stroke", "gray")
            .attr("stroke-width", 2)
            .attr("stroke-dasharray", "3,3")
            .attr("fill", (d) => {
                const dataSource = dsMap[d.records[0].dataPoint.dataSource];
                const color = EMISSION_COLORS[dataSource];
                return color ? `rgba(${color.join(",")}, 0.7)` : "#000";
            })
            .on("click", (event, d: SiteTotal) => {
                event.stopPropagation();
                setPickedEmissions(d.records);
                setPickerPosition({
                    x: event.clientX,
                    y: event.clientY,
                });
                setTimeout(() => setHoverData({}), 400);
            })
            .on("mouseover", (event, d: SiteTotal) => {
                const [px, py] = d3.pointer(event, containerRef.current);
                const rect = containerRef.current.getBoundingClientRect();
                setTooltipPosition({ x: rect.left + px, y: rect.top + py });
                setHoverData({
                    emissionId: "grouped",
                    groupedEmissionData: {
                        meanDate: d.meanDate,
                        providerName: "Bridger",
                        total: d.total,
                        numberOfGroupedEmissions: d.records.length,
                    },
                });
                const emissionIds = d.records.map((r) => r.id);
                const isSame =
                    emissionIds.length ===
                        sitePageHoverContext?.emissionIds?.length &&
                    emissionIds.every(
                        (id, i) => id === sitePageHoverContext.emissionIds?.[i],
                    );
                if (!isSame) {
                    setSitePageHoverContext({
                        emissionIds: emissionIds,
                        relatedPlumeImages: d.records.map(
                            (r) => r.dataPoint.plumeImage,
                        ),
                    });
                }
            })
            .on("mouseout", () => {
                setHoverData({});
                setSitePageHoverContext(undefined);
            })
            .style("cursor", "pointer");

        // Add scatterplot points as rectangles
        chart
            .append("g")
            .selectAll("circle")
            .data(emissions)
            .enter()
            .filter((d) => d.dataPoint.detectedRate != null)
            .append("circle")
            .style("cursor", "pointer")
            .attr("cx", (d) => xScale(d.dataPoint.detectionTimestamp))
            .attr("cy", (d) => yScaleDetection(d.dataPoint.detectedRate / 1000))
            .attr("r", 5)
            .attr("r", (d) => (selectionContext?.emissionId === d.id ? 7 : 5))
            .attr("fill", (d) => {
                const dataSource = dsMap[d.dataPoint.dataSource];
                if (!d.site) {
                    return `url(#noInfrastructurePattern-${dataSource})`;
                }
                const color = EMISSION_COLORS[dataSource];
                return color ? `rgb(${color.join(",")})` : "#000";
            })
            .attr("stroke", (d) => {
                if (selectionContext?.emissionId === d.id) {
                    return "black";
                }
                if (sitePageHoverContext?.emissionIds?.includes(d.id)) {
                    return "black";
                }
                if (!d.site) {
                    const color =
                        EMISSION_COLORS[dsMap[d.dataPoint.dataSource]];
                    return color ? `rgb(${color.join(",")})` : "#000";
                }
                return "transparent";
            })
            .attr("stroke-width", (d) => {
                if (selectionContext?.emissionId === d.id) {
                    return 2;
                }
                if (sitePageHoverContext?.emissionIds?.includes(d.id)) {
                    return 2;
                }
                return d.site ? 0 : 1;
            })
            .on("click", (event, d: EmissionRecordView) => {
                event.stopPropagation();
                const emissionTimestamp = DateTime.fromJSDate(
                    d.dataPoint.detectionTimestamp,
                );
                setSelectionContext((s) => ({
                    ...s,
                    emissionId: d.id,
                    dateRange: {
                        start: emissionTimestamp.minus({ days: 7 }).toJSDate(),
                        end: emissionTimestamp.plus({ days: 7 }).toJSDate(),
                    },
                    highlightPosition: {
                        date: emissionTimestamp.toJSDate(),
                        x: 0,
                    },
                }));
                setTimeout(() => setHoverData({}), 400);
            })
            .on("mouseover", (event, d) => {
                const [px, py] = d3.pointer(event, containerRef.current);
                const rect = containerRef.current.getBoundingClientRect();
                setTooltipPosition({ x: rect.left + px, y: rect.top + py });
                setHoverData({ emissionId: d.id });
                if (sitePageHoverContext?.emissionIds?.[0] !== d.id) {
                    setSitePageHoverContext({
                        emissionIds: [d.id],
                        relatedPlumeImages: [d.dataPoint.plumeImage],
                    });
                }
            })
            .on("mouseout", () => {
                setHoverData({});
                if (sitePageHoverContext) {
                    setSitePageHoverContext(undefined);
                }
            });

        // Handle hover events
        if (setCursorPosition) {
            // Add a background rect to handle clicks outside of points/lines
            chart
                .on("mousemove", (event) => {
                    setCursorPosition({
                        x: xScale.invert(
                            event.clientX - containerRect.left - margin.left,
                        ),
                        y: yScaleDetection.invert(
                            event.clientY - containerRect.top - margin.top,
                        ),
                    });
                })
                .on("mouseout", () => {
                    setCursorPosition(undefined);
                });

            // Show highlight line if position is set
            if (selectionContext?.highlightPosition) {
                chart
                    .append("line")
                    .attr("class", "highlight-line")
                    .attr("x1", xScale(selectionContext.highlightPosition.date))
                    .attr("x2", xScale(selectionContext.highlightPosition.date))
                    .attr("y1", 0)
                    .attr("y2", height)
                    .attr("stroke", "#3b82f6")
                    .attr("stroke-width", 1)
                    .style("stroke-dasharray", "4,4")
                    .style("pointer-events", "none");
            }
        }
    }, [
        data,
        emissions,
        nonDetects,
        groupedSiteTotals,
        logScale,
        dimensions,
        dataDateRange,
        selectionContext,
        setSelectionContext,
        setCursorPosition,
        sitePageHoverContext,
        setSitePageHoverContext,
        addZoomBrushHandle,
        zoomDomain,
    ]);

    return (
        <div ref={containerRef} className="w-full h-full relative">
            {(hoverData.emissionId || hoverData.nonDetectId) &&
                tooltipPosition &&
                createPortal(
                    <EmissionPlotHover
                        emissionId={hoverData.emissionId}
                        groupedEmissionData={hoverData.groupedEmissionData}
                        nonDetectId={hoverData.nonDetectId}
                        style={{
                            position: "fixed",
                            left: tooltipPosition.x,
                            top: tooltipPosition.y,
                            pointerEvents: "none",
                            transform:
                                "translate(-50%, -100%) translateY(-8px)",
                            zIndex: 9999,
                        }}
                    />,
                    document.body,
                )}
            <svg ref={svgRef} style={{ width: "100%", height: "100%" }} />
            {/* Reset zoom button - only show when zoomed or date range selected */}
            {(currentTransform.k !== 1 ||
                currentTransform.x !== 0 ||
                currentTransform.y !== 0 ||
                zoomDomain.x !== null ||
                zoomDomain.y !== null ||
                selectionContext?.dateRange) && (
                <button
                    type="button"
                    onClick={() => {
                        resetZoom();
                    }}
                    className="absolute top-2 right-2 px-3 py-1 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 transition-colors text-sm"
                    title="Reset zoom"
                >
                    Reset Zoom
                </button>
            )}

            {pickedEmissions.length > 0 &&
                pickerPosition &&
                createPortal(
                    <div
                        ref={pickerRef as Ref<HTMLDivElement>}
                        className="fixed bg-white border border-gray-300 rounded-lg shadow-lg z-[10000] w-64"
                        style={{
                            left: pickerPosition.x,
                            top: pickerPosition.y,
                        }}
                    >
                        <div className="max-h-[200px] overflow-y-auto">
                            <div className="text-sm px-2 py-1 rounded-t-lg bg-ae-neutral-100">
                                Total emissions:{" "}
                                {(
                                    pickedEmissions.reduce(
                                        (sum, p) =>
                                            sum +
                                            (p.dataPoint.detectedRate || 0),
                                        0,
                                    ) / 1000
                                ).toFixed(2)}
                                {" kg/h"}
                            </div>
                            {pickedEmissions
                                .toSorted(
                                    (a, b) =>
                                        b.dataPoint.detectedRate -
                                        a.dataPoint.detectedRate,
                                )
                                .map((emission) => {
                                    const isSelected =
                                        selectionContext?.emissionId ===
                                        emission.id;
                                    const dataSource =
                                        dsMap[emission.dataPoint.dataSource];
                                    const color = EMISSION_COLORS[dataSource];
                                    const colorStyle = color
                                        ? `rgb(${color.join(",")})`
                                        : "#000";

                                    return (
                                        <button
                                            type="button"
                                            key={emission.id}
                                            onClick={() => {
                                                const emissionTimestamp =
                                                    DateTime.fromJSDate(
                                                        emission.dataPoint
                                                            .detectionTimestamp,
                                                    );
                                                setSelectionContext((s) => ({
                                                    ...s,
                                                    emissionId: emission.id,
                                                    dateRange: {
                                                        start: emissionTimestamp
                                                            .minus({ days: 7 })
                                                            .toJSDate(),
                                                        end: emissionTimestamp
                                                            .plus({ days: 7 })
                                                            .toJSDate(),
                                                    },
                                                    highlightPosition: {
                                                        date: emissionTimestamp.toJSDate(),
                                                        x: 0,
                                                    },
                                                }));
                                                setPickerPosition(undefined);
                                                setPickedEmissions([]);
                                            }}
                                            className={`w-full text-left px-2 py-1 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 ${
                                                isSelected ? "bg-blue-50" : ""
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <div
                                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                                    style={{
                                                        backgroundColor:
                                                            colorStyle,
                                                    }}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {emission.dataPoint
                                                            .detectedRate
                                                            ? `${(emission.dataPoint.detectedRate / 1000).toFixed(2)} kg/hr`
                                                            : "No rate"}
                                                    </div>
                                                    <div className="text-xs text-gray-500 truncate">
                                                        {formatDateTime(
                                                            emission.dataPoint
                                                                .detectionTimestamp,
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
};
